'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Fragment} from 'react';
import {Typography} from '../../primitives';
import type {ListingCardMetaItem} from './types';

export interface ListingCardPrimaryMetaProps {
  item: ListingCardMetaItem;
}

export interface ListingCardMetaProps {
  items: ListingCardMetaItem[];
}

const PrimaryMetaRow = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(0.25),
  color: theme.palette.colors.text.primary.default,
  '& svg': {color: theme.palette.colors.icon.primary.default},
}));

export function ListingCardPrimaryMeta({item}: ListingCardPrimaryMetaProps) {
  return (
    <PrimaryMetaRow>
      {item.icon}
      <Typography as="span" size="body-2" weight="regular" sx={{color: 'inherit'}}>
        {item.label}
      </Typography>
    </PrimaryMetaRow>
  );
}

ListingCardPrimaryMeta.displayName = 'ListingCardPrimaryMeta';

const MetaRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  color: theme.palette.colors.text.neutral.muted,
}));

const MetaItem = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.palette.colors.icon.primary.default,
  gap: theme.spacing(0.5),
  '& svg': {fontSize: 14},
}));

const MetaDivider = styled('span')(({theme}) => ({
  width: 1,
  alignSelf: 'stretch',
  background: theme.palette.colors.border.neutral.default,
  marginInline: theme.spacing(1.5),
}));

export function ListingCardMeta({items}: ListingCardMetaProps) {
  if (items.length === 0) {
    return null;
  }
  return (
    <MetaRow>
      {items.map((item, index) => (
        <Fragment key={`${item.label}-${index}`}>
          {index > 0 && <MetaDivider />}
          <MetaItem>
            {item.icon}
            <Typography
              as="span"
              size="body-2"
              weight="regular"
              mobileSize="body-3"
              sx={theme => ({color: theme.palette.colors.text.neutral.default})}
            >
              {item.label}
            </Typography>
          </MetaItem>
        </Fragment>
      ))}
    </MetaRow>
  );
}

ListingCardMeta.displayName = 'ListingCardMeta';
